import { shallowRef, markRaw, type Component, type ShallowRef } from "vue";

export type DialogAction = "success" | "cancel" | "dismiss" | "switch";

export interface DialogResult<T = void> {
    action: DialogAction;
    data?: T;
    switchTo?: string;
}

export interface DialogOpenOptions {
    priority?: number;
}

export interface DialogEntry<
    P extends Record<string, unknown> = Record<string, unknown>,
    R = unknown,
> {
    id: string;
    component: Component;
    props: P;
    priority: number;
    resolve: (result: DialogResult<R>) => void;
}

type DialogStack = DialogEntry<Record<string, unknown>, unknown>[];

const dialogStack: ShallowRef<DialogStack> = shallowRef([]);
let dialogIdCounter = 0;

function generateDialogId(): string {
    dialogIdCounter += 1;
    return `dialog-${dialogIdCounter}`;
}

export function useDialogService() {
    function open<P extends Record<string, unknown>, R = void>(
        component: Component,
        props?: P,
        options?: DialogOpenOptions,
    ): Promise<DialogResult<R>> {
        return new Promise((resolve) => {
            const entry: DialogEntry<P, R> = {
                id: generateDialogId(),
                component: markRaw(component),
                props: props ?? ({} as P),
                priority: options?.priority ?? 0,
                resolve,
            };

            dialogStack.value = [...dialogStack.value, entry as DialogEntry];
        });
    }

    function close<R = void>(action: DialogAction, data?: R): void {
        const top = getTopDialog();
        if (!top) return;

        dialogStack.value = dialogStack.value.filter((entry) => entry.id !== top.id);
        top.resolve({ action, data } as DialogResult);
    }

    function success<R = void>(data?: R): void {
        close("success", data);
    }

    function cancel(): void {
        close("cancel");
    }

    function dismiss(): void {
        close("dismiss");
    }

    function switchTo(dialogName: string): void {
        close("switch", { switchTo: dialogName } as unknown);
    }

    function getTopDialog(): DialogEntry | null {
        const stack = dialogStack.value;
        if (stack.length === 0) return null;
        let top = stack[0]!;
        for (let i = 1; i < stack.length; i++) {
            const entry = stack[i]!;
            if (entry.priority >= top.priority) top = entry;
        }
        return top;
    }

    function hasOpenDialogs(): boolean {
        return dialogStack.value.length > 0;
    }

    return {
        dialogStack,
        open,
        close,
        success,
        cancel,
        dismiss,
        switchTo,
        getTopDialog,
        hasOpenDialogs,
    };
}
